import React, { useRef, useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Typography, Box, Button, CircularProgress } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useTheme } from '@emotion/react';
import { v4 as uuid } from 'uuid';

import { openSnackbar } from 'store/application';
import { Container } from 'shared-components/Container';
import { OutlineButton } from 'shared-components/Buttons/Outline';
import { uploadImage } from 'utils/imageUpload';
import { MAX_PROFILE_IMAGE_SIZE_IN_MB } from 'constants';

import { useProfileImage } from 'context/ProfileImageContext';
import DefaultProfileAvatar from 'images/DefaultProfileAvatar.png';
import { selectAppConfig } from 'store/initialize';
import { useUpdateAuth0MetadataMutation } from 'store/api/pkd/endpoints/user';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';

const profileImageId = uuid();
const allowedFileTypes = ['image/png', 'image/jpg', 'image/jpeg'];
export const ProfilePicture = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    azureStorageBlobToken: encodedAzureStorageBlobToken,
    azureStorageBlobSasUrl: encodedAzureStorageBlobSasUrl,
    azureStorageBaseURL,
  } = useSelector(selectAppConfig());

  const azureStorageBlobToken = decodeURIComponent(
    encodedAzureStorageBlobToken
  );
  const azureStorageBlobSASUrl = decodeURIComponent(
    encodedAzureStorageBlobSasUrl
  );

  const { isLoading: auth0IsLoading } = useAuth0();
  const { isCurrentPersonLoading, userId } = useGetPersonAndAttributes();
  const [updateAuth0Metadata] = useUpdateAuth0MetadataMutation();
  const {
    profileImageUrl,
    uploadedImageUrl,
    handleSetProfileImageUrl,
    handleSetHeaderImageUrl,
  } = useProfileImage();

  const profileImageInput = useRef(null);
  const profilePreviewRef = useRef(null);

  const [profileImage, setProfileImage] = useState();
  const [uploadLoading, setUploadLoading] = useState(false);
  const [hasUploadedImage, setHasUploadedImage] = useState(false);

  const handleClick = () => {
    profileImageInput.current.click();
  };
  const handleProfileImageChange = async (event) => {
    const fileUploaded = event.target.files[0];
    const fileType = fileUploaded.type;
    if (!allowedFileTypes.includes(fileType)) {
      return dispatch(
        openSnackbar({
          type : 'error',
          message :
            'You tried uploading an invalid file format. Allowed formats: PNG, JPEG, JPG',
        })
      );
    }
    if (fileUploaded) {
      const fileObject = URL.createObjectURL(fileUploaded);
      const file = new Image();
      file.src = fileObject;
      file.onload = () => {
        const fileSize = (fileUploaded.size / (1024 * 1024)).toFixed(2); // convert to MB
        if (fileSize > MAX_PROFILE_IMAGE_SIZE_IN_MB) {
          return dispatch(
            openSnackbar({
              type : 'error',
              message :
                'Profile image too big! Your profile image must not be bigger than 5MB and not more than 300px wide',
            })
          );
        }
        setHasUploadedImage(true);
        setProfileImage(fileUploaded);
        handleSetProfileImageUrl(fileObject);
      };
    }
  };

  const handleSaveProfileImage = async () => {
    setUploadLoading(true);
    const fileExtension = 'png';
    const filename = `profile_images/${profileImageId}.${fileExtension}`;
    const fileType = profileImage.type;
    const modifiedImage = new File([profileImage], filename, {
      type : fileType,
    });
    try {
      await uploadImage(
        modifiedImage,
        azureStorageBlobSASUrl,
        azureStorageBlobToken
      );
      // Save to auth0
      const userMetadata = {
        picture : `${azureStorageBaseURL}/patient-files/${filename}`,
      };
      const { error, data } = await updateAuth0Metadata({
        userId,
        userMetadata,
      });

      const url = `${userMetadata.picture}?${azureStorageBlobToken}`;
      handleSetProfileImageUrl(url);
      handleSetHeaderImageUrl(profileImageUrl);
      if (error) {
        return dispatch(
          openSnackbar({
            type    : 'error',
            message : 'Could not save your profile image. Please try again',
          })
        );
      } else {
        dispatch(
          openSnackbar({
            type    : 'success',
            message : 'Profile picture successfully saved',
          })
        );
      }
      setHasUploadedImage(false);
    } catch (error) {
      dispatch(
        openSnackbar({
          type    : 'error',
          message : 'An unexpected error occured. Try again later',
        })
      );
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <Container
      maxWidth={{ xl: '800px' }} mb={4}>
      <Typography
        fontFamily='Montserrat' fontWeight='700' variant='h6' mb={4}>
        Profile Picture
      </Typography>

      <Box
        sx={{
          display       : 'flex',
          flexDirection : 'row',
          p             : '2rem 1rem',
        }}>
        {profileImageUrl !== ''
          ? (
            <Box
              display='flex' flexDirection='column'>
              <img
                ref={profilePreviewRef}
                src={uploadedImageUrl}
                alt='Avatar'
                width='100'
              />
              {hasUploadedImage && (
                <Button
                  variant='contained'
                  sx={{
                    fontSize        : 13,
                    textTransform   : 'capitalize',
                    mt              : 2,
                    backgroundColor : '#00778B',
                  }}
                  onClick={handleSaveProfileImage}
                  disabled={uploadLoading}>
                Save
                  {uploadLoading && (
                    <CircularProgress
                      sx={{ marginLeft: 1 }}
                      color='inherit'
                      size={15}
                    />
                  )}
                </Button>
              )}
            </Box>
          )
          : (
            <img
              src={DefaultProfileAvatar} alt='Default Avatar' width='100'
            />
          )}
        <Box
          sx={{
            display       : 'flex',
            flexDirection : 'column',
            ml            : 4,
          }}>
          <OutlineButton
            width='70%'
            fontFamily='Montserrat'
            color={theme.palette.primary.blue1}
            borderColor={theme.palette.primary.blue1}
            text='Upload picture'
            disabled={isCurrentPersonLoading || auth0IsLoading}
            onClick={handleClick}
          />
          <input
            type='file'
            ref={profileImageInput}
            onChange={handleProfileImageChange}
            onClick={(event) => {
              event.target.value = null;
            }}
            style={{ display: 'none' }}
            accept='.png, .jpg, .jpeg'
            data-testid={'profile-image-input'}
          />
          <Typography
            variant='body2' mt={2}>
            Maximum 5MB size recommended.
            <br />
            Supported formats: PNG, JPG, JPEG
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};
